import { Button } from "@/components/ui/button";
import type {
	NavigationAufgabenstellungDTO,
	NavigationKlausurDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";
import {
	ArrowUpDown,
	ChevronDown,
	ChevronRight,
	ChevronUp,
	GripVertical,
} from "lucide-react";
import { Plus } from "lucide-react";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useKlausurEditing } from "@/features/klausur/context/klausurEditingProvider";
import { useKlausurEditorScroll } from "../context/klausurEditorScrollProvider";
import { useKlausurSidebarSelection } from "../context/klausurSidebarSelectionContext";
import { useKlausurErstellerSidebarActions } from "@/features/klausur/context/klausurErstellerSidebarActionsProvider";
import { useOrderingModal } from "@/features/klausur/context/orderingModalProvider";

/**
 * Props for the KlausurErstellerSidebar component
 */
interface KlausurErstellerSidebarProps {
	klausur: NavigationKlausurDTO;
	onSachverhaltClick?: (sachverhalt: NavigationSachverhaltDTO) => void;
	onAufgabenstellungClick?: (
		aufgabenstellung: NavigationAufgabenstellungDTO,
	) => void;
}

/**
 * Sidebar for the KlausurErstellerPage
 * - displays the klausur contents
 * - allows to expand and collapse the aufgabenstellungen
 * - allows to reorder the sachverhalte
 * - allows dragging and dropping the sachverhalte to reorder them
 */
export default function KlausurErstellerSidebar({
	klausur,
	onSachverhaltClick,
	onAufgabenstellungClick,
}: KlausurErstellerSidebarProps) {
	const {
		expandedAufgabenstellungen,
		toggleAufgabenstellung,
		toggleAllAufgabenstellungen,
		navigationKlausur,
		setNavigationKlausur,
		setSelectedAufgabenstellung,
		setSelectedSachverhalt,
		selectedAufgabenstellungId,
	} = useKlausurSidebarSelection();
	const {
		openCreateAufgabenstellungModal,
		openCreateSachverhaltModal,
		updateSachverhalteIndexes,
	} = useKlausurEditing();
	const { scrollToElement } = useKlausurEditorScroll();

	const { openDeleteAufgabe, openDeleteSachverhalt } =
		useKlausurErstellerSidebarActions();
	const { openAufgabenstellungenOrdering } = useOrderingModal();

	useEffect(() => {
		setNavigationKlausur(klausur);
	}, [klausur, setNavigationKlausur]);

	const localKlausur = navigationKlausur ?? klausur;

	const allExpanded =
		expandedAufgabenstellungen.size ===
			localKlausur.aufgabenstellungen.length &&
		localKlausur.aufgabenstellungen.length > 0;

	return (
		<div className="w-64 min-w-64 flex-shrink-0 bg-gradient-to-b from-muted/30 to-muted/10 h-full max-h-full overflow-y-auto border-r border-border/50">
			<div className="p-4 space-y-4">
				{/* Header Section */}
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-bold sidebar-text-safe tracking-tight">
							Inhaltsverzeichnis
						</h2>
						<div className="flex items-center gap-1">
							<Button
								variant="ghost"
								size="sm"
								onClick={toggleAllAufgabenstellungen}
								title={allExpanded ? "Alle einklappen" : "Alle ausklappen"}
								className="h-8 w-8 p-0 hover:bg-primary/10 transition-colors"
							>
								{allExpanded ? (
									<ChevronUp className="h-4 w-4" />
								) : (
									<ChevronDown className="h-4 w-4" />
								)}
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									openAufgabenstellungenOrdering(
										localKlausur.aufgabenstellungen
											.slice()
											.sort((a, b) => a.index - b.index)
											.map((a) => ({
												id: a.id,
												label: a.title || "Ohne Titel",
												index: a.index,
											})),
									)
								}
								title="Reihenfolge der Aufgabenstellungen ändern"
								className="h-8 w-8 p-0 hover:bg-primary/10 transition-colors"
							>
								<ArrowUpDown className="h-4 w-4" />
							</Button>
						</div>
					</div>
					<Button
						variant="outline"
						onClick={() => openCreateAufgabenstellungModal()}
						className="w-full sidebar-text-safe shadow-sm hover:shadow-md hover:bg-primary/5 transition-all duration-200"
					>
						<Plus className="h-4 w-4 mr-2" />
						Neue Aufgabenstellung
					</Button>
				</div>

				{/* Content Section */}
				<div className="space-y-2.5">
					{localKlausur.aufgabenstellungen.map(
						(aufgabe: NavigationAufgabenstellungDTO, aIdx: number) => (
							<div key={aufgabe.id} className="space-y-1">
								<div
									className={`group relative flex items-center gap-2 p-2.5 rounded-lg transition-all duration-200 cursor-pointer ${
										selectedAufgabenstellungId === aufgabe.id
											? "bg-primary text-primary-foreground shadow-md scale-[1.02]"
											: "bg-background/60 hover:bg-background/90 hover:shadow-sm border border-border/40 hover:border-border/60"
									}`}
									onClick={() => {
										setSelectedAufgabenstellung(aufgabe.id);
										if (onAufgabenstellungClick)
											onAufgabenstellungClick(aufgabe);
										scrollToElement(aufgabe.id, "instant");
									}}
								>
									<div className="font-semibold text-sm flex-grow text-left sidebar-text-safe min-w-0 truncate">
										<span className="inline-block w-6 opacity-70">
											{aIdx + 1}.
										</span>
										<span>{aufgabe.title || "Ohne Titel"}</span>
									</div>
									<div className="flex items-center gap-0.5 flex-shrink-0">
										<Button
											variant="ghost"
											size="icon"
											className={`h-7 w-7 transition-colors ${
												selectedAufgabenstellungId === aufgabe.id
													? "hover:bg-primary-foreground/20"
													: "hover:bg-muted"
											}`}
											onClick={(e) => {
												e.stopPropagation();
												toggleAufgabenstellung(aufgabe.id);
											}}
										>
											{expandedAufgabenstellungen.has(aufgabe.id) ? (
												<ChevronDown className="h-4 w-4" />
											) : (
												<ChevronRight className="h-4 w-4" />
											)}
										</Button>
										<Button
											variant="ghost"
											size="icon"
											title="Aufgabenstellung löschen"
											className={`h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity ${
												selectedAufgabenstellungId === aufgabe.id
													? "text-primary-foreground hover:bg-destructive/20"
													: "text-destructive hover:bg-destructive/10"
											}`}
											onClick={(e) => {
												e.stopPropagation();
												openDeleteAufgabe(aufgabe);
											}}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
								{expandedAufgabenstellungen.has(aufgabe.id) && (
									<div className="animate-in slide-in-from-top-2 duration-200">
										<SachverhalteList
											aufgabe={aufgabe}
											aufgabenstellungIndex={aIdx}
											onSachverhaltClick={(s) => {
												console.log("Sidebar.onSachverhaltClick", s.id);
												setSelectedSachverhalt(s.id);
												if (onSachverhaltClick) onSachverhaltClick(s);
												scrollToElement(s.id);
											}}
											onReorder={(newList) => {
												// Local preview is handled by provider's optimistic update
												// Delegate persistence to provider
												updateSachverhalteIndexes(aufgabe.id, newList);
											}}
											onDeleteSachverhalt={(aId, s) =>
												openDeleteSachverhalt(aId, s)
											}
										/>
										<div className="ml-6 mt-1.5">
											<Button
												variant="ghost"
												size="sm"
												className="w-full justify-start text-xs h-8 hover:bg-primary/5 text-muted-foreground hover:text-foreground transition-colors"
												onClick={() => openCreateSachverhaltModal(aufgabe.id)}
											>
												<Plus className="h-3.5 w-3.5 mr-1.5" />
												Neuer Sachverhalt
											</Button>
										</div>
									</div>
								)}
							</div>
						),
					)}
				</div>
			</div>
		</div>
	);
}

/**
 * Item for the SachverhalteList component
 * - displays the sachverhalt
 * - allows to drag and drop the sachverhalt
 * - allows to click the sachverhalt
 */
function SortableSachverhaltItem(props: {
	sachverhalt: NavigationSachverhaltDTO;
	labelPrefix: string;
	onClick?: (sv: NavigationSachverhaltDTO) => void;
	onDelete?: (sv: NavigationSachverhaltDTO) => void;
}) {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id: props.sachverhalt.id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};
	const { selectedSachverhaltId } = useKlausurSidebarSelection();
	const isActive = selectedSachverhaltId === props.sachverhalt.id;

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`group flex items-center gap-1.5 p-2 rounded-md transition-all duration-150 cursor-pointer ${
				isActive
					? "bg-primary/70 text-primary-foreground shadow-sm"
					: "bg-background/40 hover:bg-background/80 border border-border/30 hover:border-border/50 hover:shadow-sm"
			}`}
			onClick={() => props.onClick?.(props.sachverhalt)}
		>
			<div
				{...attributes}
				{...listeners}
				className={`cursor-grab active:cursor-grabbing p-1 -ml-1 rounded transition-all ${
					isActive
						? "opacity-70 hover:opacity-100 hover:bg-primary-foreground/10"
						: "opacity-40 hover:opacity-70 hover:bg-muted/70"
				}`}
				onClick={(e) => e.stopPropagation()}
			>
				<GripVertical className="h-3.5 w-3.5" />
			</div>
			<div className="text-sm flex-grow text-left sidebar-text-safe min-w-0 truncate py-0.5">
				<span className="font-medium opacity-80 mr-1.5">
					{props.labelPrefix}
				</span>
				<span>{props.sachverhalt.title || "Ohne Titel"}</span>
			</div>
			<Button
				variant="ghost"
				size="icon"
				title="Sachverhalt löschen"
				className={`h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
					isActive
						? "text-primary-foreground hover:bg-destructive/20"
						: "text-destructive hover:bg-destructive/10"
				}`}
				onClick={(e) => {
					e.stopPropagation();
					props.onDelete?.(props.sachverhalt);
				}}
			>
				<Trash2 className="h-3 w-3" />
			</Button>
		</div>
	);
}

/**
 * List of sachverhalte for the SachverhalteList component
 * - displays the sachverhalte
 * - allows to drag and drop the sachverhalte
 * - allows to click the sachverhalt
 */
function SachverhalteList(props: {
	aufgabe: NavigationAufgabenstellungDTO;
	aufgabenstellungIndex: number;
	onSachverhaltClick?: (s: NavigationSachverhaltDTO) => void;
	onReorder: (newList: NavigationSachverhaltDTO[]) => void;
	onDeleteSachverhalt?: (
		aufgabenstellungId: string,
		s: NavigationSachverhaltDTO,
	) => void;
}) {
	// no-op
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	/**
	 * Handle the drag end event
	 * @param event - The drag end event
	 */
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = props.aufgabe.sachverhalte.findIndex(
			(s) => s.id === active.id,
		);
		const newIndex = props.aufgabe.sachverhalte.findIndex(
			(s) => s.id === over.id,
		);
		if (oldIndex === -1 || newIndex === -1) return;
		const reordered = arrayMove(
			[...props.aufgabe.sachverhalte],
			oldIndex,
			newIndex,
		).map((sv, idx) => ({
			...sv,
			index: idx,
		}));
		props.onReorder(reordered);
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={props.aufgabe.sachverhalte.map((s) => s.id)}
				strategy={verticalListSortingStrategy}
			>
				<div className="ml-6 space-y-1.5 pl-3 relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-border/60 before:via-border/40 before:to-border/20">
					{props.aufgabe.sachverhalte.map((sv, sIdx) => (
						<SortableSachverhaltItem
							key={sv.id}
							sachverhalt={sv}
							labelPrefix={`${props.aufgabenstellungIndex + 1}.${sIdx + 1}`}
							onClick={(s) => props.onSachverhaltClick?.(s)}
							onDelete={(s) => props.onDeleteSachverhalt?.(props.aufgabe.id, s)}
						/>
					))}
				</div>
			</SortableContext>
		</DndContext>
	);
}
